import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { entries } from "@/mocks/entries";
import { siteConfig, stats } from "@/mocks/site";

const trending = [
  "短剧剧本相似度",
  "音乐节退票方案",
  "应援色规范",
  "直播赛事核验",
  "综艺剪辑争议",
  "选品流程复盘",
];

export default function Hero() {
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const featured = entries[0];

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/entries?q=${encodeURIComponent(q)}` : "/entries");
  };

  return (
    <section className="relative w-full border-b border-background-200 bg-background-50">
      <div className="px-4 pb-14 pt-12 md:px-10 md:pb-20 md:pt-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
              <i className="ri-flashlight-line" />
              事件词条库 · 每日两次更新
            </span>

            <h1 className="mt-6 font-heading text-4xl font-black leading-[1.15] tracking-tight text-foreground-950 md:text-6xl">
              深挖每一个瓜条
              <br />
              背后的<span className="text-primary-600">真相与脉络</span>
            </h1>

            <p className="mt-5 max-w-2xl text-base leading-relaxed text-foreground-600 md:text-lg">
              {siteConfig.name}把网络热点整理成结构化词条：事件的来龙去脉、关键时间线、各方公开回应，
              以及一段偏向理性的观察。你想知道的，这里按顺序讲清楚。
            </p>

            <form onSubmit={onSubmit} className="mt-8 flex w-full max-w-xl flex-col gap-3 sm:flex-row">
              <div className="relative flex-1">
                <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400" />
                <input
                  type="search"
                  name="q"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索事件、人物、话题关键词"
                  aria-label="搜索事件词条"
                  className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-10 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <button
                type="submit"
                className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索词条
              </button>
            </form>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-foreground-500">
              <span className="font-medium text-foreground-700">热门搜索：</span>
              {trending.slice(0, 4).map((item) => (
                <Link
                  key={item}
                  to={`/entries?q=${encodeURIComponent(item)}`}
                  className="rounded-full border border-background-300 px-3 py-1 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  {item}
                </Link>
              ))}
            </div>

            <div className="mt-10 grid grid-cols-2 gap-6 border-t border-background-200 pt-8 sm:grid-cols-4">
              {stats.map((item) => (
                <div key={item.label}>
                  <div className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                    {item.value}
                  </div>
                  <div className="mt-1 text-xs text-foreground-500">{item.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <Link
              to={`/entry/${featured.slug}`}
              className="group block overflow-hidden rounded-lg border border-background-200 bg-background-100"
            >
              <div className="relative w-full" style={{ height: "260px" }}>
                <img
                  src={featured.cover}
                  alt={`${featured.title} — 本周头条事件词条`}
                  title={`${featured.title} — 黑料百科事件详解`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-3 top-3 rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
                  本周头条
                </span>
              </div>
              <div className="p-5 md:p-6">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
                  <span>{featured.categoryName}</span>
                  <span className="text-foreground-300">·</span>
                  <span>更新于 {featured.updatedAt}</span>
                </div>
                <h2 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950 md:text-xl">
                  {featured.title}
                </h2>
                <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                  {featured.summary}
                </p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600">
                  查看完整记录
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </div>

      <div className="flex w-full items-center overflow-hidden border-t border-background-200 bg-foreground-950 py-3">
        <div className="flex min-w-full shrink-0 animate-ticker items-center gap-8 pl-8">
          {[...trending, ...trending].map((item, index) => (
            <span
              key={`${item}-${index}`}
              className="flex shrink-0 items-center gap-8 whitespace-nowrap text-sm text-background-100"
            >
              {item}
              <i className="ri-flashlight-fill text-accent-400" />
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}